import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { supabase } from '../lib/supabaseClient'
import { authTranslations } from '../translations/translations'

const Auth = () => {
  const { signIn, signUp } = useAuth()
  const { language } = useLanguage()
  const navigate = useNavigate()
  const [mode, setMode] = useState('signin') // 'signin' | 'signup'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('RU')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [canResend, setCanResend] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const countries = {
    RU: { dial: '7', placeholder: '+7 (___) ___-__-__' },
    ES: { dial: '34', placeholder: '+34 ___ __ __ __' },
    US: { dial: '1', placeholder: '+1 (___) ___-____' }
  }

  const onlyDigits = (s) => (s || '').replace(/\D/g, '')

  const prefixFor = (c) => `+${countries[c]?.dial || ''} `
  const stripAnyPrefix = (v) => v.replace(/^\+\d+\s*/, '')

  const formatLocal = (digits, c) => {
    const d = (digits || '').slice(0, c === 'US' ? 10 : c === 'RU' ? 10 : 9)
    if (c === 'RU') {
      const p1 = d.slice(0,3)
      const p2 = d.slice(3,6)
      const p3 = d.slice(6,8)
      const p4 = d.slice(8,10)
      let out = ''
      if (p1) out += `(${p1}${p1.length===3 ? ')' : ''}`
      if (p2) out += `${p1 ? ' ' : ''}${p2}`
      if (p3) out += `-${p3}`
      if (p4) out += `-${p4}`
      return out
    }
    if (c === 'US') {
      const p1 = d.slice(0,3)
      const p2 = d.slice(3,6)
      const p3 = d.slice(6,10)
      let out = ''
      if (p1) out += `(${p1}${p1.length===3 ? ')' : ''}`
      if (p2) out += `${p1 ? ' ' : ''}${p2}`
      if (p3) out += `-${p3}`
      return out
    }
    // ES default: 9 digits -> 3 2 2 2 spaced
    const p1 = d.slice(0,3)
    const p2 = d.slice(3,5)
    const p3 = d.slice(5,7)
    const p4 = d.slice(7,9)
    return [p1,p2,p3,p4].filter(Boolean).join(' ')
  }

  const toE164 = (input, c) => {
    const cfg = countries[c]
    if (!cfg) return input
    const d = onlyDigits(input)
    let local = d
    if (local.startsWith(cfg.dial)) local = local.slice(cfg.dial.length)
    if (c === 'RU' && local.startsWith('8')) local = local.slice(1)
    return `+${cfg.dial}${local}`
  }

  const t = {
    title: { ru: 'Аккаунт', en: 'Account', es: 'Cuenta' },
    email: { ru: 'Email', en: 'Email', es: 'Email' },
    password: { ru: 'Пароль', en: 'Password', es: 'Contraseña' },
    phone: { ru: 'Телефон', en: 'Phone', es: 'Teléfono' },
    signin: { ru: 'Войти', en: 'Sign In', es: 'Iniciar sesión' },
    signup: { ru: 'Создать аккаунт', en: 'Sign Up', es: 'Crear cuenta' },
    haveAcc: { ru: 'Уже есть аккаунт? Войти', en: 'Have an account? Sign In', es: '¿Ya tiene cuenta? Iniciar sesión' },
    noAcc: { ru: 'Нет аккаунта? Создать', en: "Don't have an account? Sign Up", es: '¿No tiene cuenta? Crear' },
    confirmEmailInfo: {
      ru: 'Мы отправили письмо на вашу почту. Перейдите по ссылке в письме, чтобы подтвердить email, затем войдите.',
      en: 'We sent an email to you. Open the link in the message to confirm your email, then sign in.',
      es: 'Te enviamos un correo. Abre el enlace del mensaje para confirmar tu email y luego inicia sesión.'
    },
    emailNotConfirmed: {
      ru: 'Email не подтверждён. Проверьте почту и перейдите по ссылке для подтверждения.',
      en: 'Email not confirmed. Please check your inbox and confirm your email.',
      es: 'Email no confirmado. Revisa tu buzón y confirma tu email.'
    },
    resend: {
      ru: 'Отправить письмо ещё раз',
      en: 'Resend confirmation email',
      es: 'Reenviar correo de confirmación'
    },
    resentOk: {
      ru: 'Письмо отправлено повторно. Проверьте почту.',
      en: 'Confirmation email resent. Please check your inbox.',
      es: 'Correo de confirmación reenviado. Revisa tu buzón.'
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setInfo('')
    try {
      if (mode === 'signin') {
        const { data, error } = await signIn({ email, password })
        if (error) {
          // TEMP: email confirmation check disabled
          // const msg = (error.message || '').toLowerCase()
          // if (msg.includes('email') && msg.includes('confirm')) {
          //   setError(t.emailNotConfirmed[language])
          //   setCanResend(true)
          //   return
          // }
          throw error
        }
        if (!data || !data.session) {
          setError((authTranslations.errors.invalidCredentials[language]) || authTranslations.errors.invalidCredentials.en)
          return
        }
        // Notify header to close any open menus, then navigate home
        try { window.dispatchEvent(new Event('auth:signedin')) } catch {}
        navigate('/')
      } else {
        // Validate phone required
        const rawDigits = onlyDigits(stripAnyPrefix(phone))
        if (!rawDigits || rawDigits.length < 7) {
          setError(language === 'ru' ? 'Введите корректный номер телефона' : language === 'es' ? 'Introduzca un número de teléfono válido' : 'Please enter a valid phone number')
          return
        }
        const e164Phone = toE164(phone, country)
        const endpoint = process.env.VITE_SIGNUP_ENDPOINT
        if (endpoint) {
          const resp = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, phone: e164Phone })
          })
          const json = await resp.json().catch(()=>({}))
          if (!resp.ok) throw new Error(json?.error || 'Signup failed')
        } else {
          const { data, error } = await signUp({ email, password, phone: e164Phone })
          if (error) throw error
        }
        // Sign in to create session
        const { data: signInData, error: signInErr } = await signIn({ email, password })
        if (signInErr) throw signInErr
        if (!endpoint) {
          // If using client signUp path, ensure profile exists post-login
          const authedUserId = signInData?.user?.id
          if (authedUserId) {
            const { error: profErr } = await supabase
              .from('profiles')
              .upsert({ id: authedUserId, email, phone: e164Phone }, { onConflict: 'id' })
            if (profErr) throw profErr
          }
        }
        try { window.dispatchEvent(new Event('auth:signedin')) } catch {}
        navigate('/')
      }
    } catch (err) {
      const raw = err?.message || 'Error'
      const m = raw.match(/after\s+(\d+)\s+seconds?/i)
      if (m) {
        const seconds = m[1]
        const tpl = authTranslations.errors.rateLimitWait[language] || authTranslations.errors.rateLimitWait.en
        setError(tpl.replace('{seconds}', seconds))
      } else {
        setError(raw)
      }
    } finally {
      setLoading(false)
    }
  }

  const resendEmail = async () => {
    setLoading(true)
    setError('')
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email })
      if (error) throw error
      setInfo(t.resentOk[language])
    } catch (err) {
      const raw = err?.message || 'Error'
      const m = raw.match(/after\s+(\d+)\s+seconds?/i)
      if (m) {
        const seconds = m[1]
        const tpl = authTranslations.errors.rateLimitWait[language] || authTranslations.errors.rateLimitWait.en
        setError(tpl.replace('{seconds}', seconds))
      } else {
        setError(raw)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-12 pt-0">
        <h1 className="text-[34px] leading-tight md:text-4xl font-light mb-6 playfair tracking-wide">
          <span className="inline-flex items-center gap-3">
            <i className="ri-user-3-line text-[22px] md:text-2xl"></i>
            {t.title[language]}
          </span>
        </h1>
        <div className="relative overflow-hidden rounded-2xl bg-white border border-gray-200/70 shadow-[0_6px_30px_-10px_rgba(0,0,0,0.2)]">
          <div className="p-6 md:p-8">
          <form onSubmit={onSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              placeholder={t.email[language]}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 hover:bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-black/10 montserrat"
            />
            {mode==='signup' && (
              <div className="flex gap-2 items-stretch w-full">
                <select
                  value={country}
                  onChange={(e)=>{
                    const c = e.target.value
                    const newPrefix = prefixFor(c)
                    const restDigits = onlyDigits(stripAnyPrefix(phone))
                    setCountry(c)
                    setPhone(newPrefix + formatLocal(restDigits, c))
                  }}
                  className="w-24 sm:w-28 flex-none px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 hover:bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-black/10 montserrat"
                >
                  <option value="RU">RU +7</option>
                  <option value="ES">ES +34</option>
                  <option value="US">US +1</option>
                </select>
                <input
                  type="tel"
                  value={phone}
                  onFocus={()=>{
                    const p = prefixFor(country)
                    if (!phone || !phone.startsWith(p)) setPhone(p)
                  }}
                  onKeyDown={(e)=>{
                    if (e.key === 'Backspace') {
                      const p = prefixFor(country)
                      const pos = e.currentTarget.selectionStart ?? 0
                      if (pos <= p.length) {
                        e.preventDefault()
                        return
                      }
                      // If caret at end, delete last digit explicitly
                      if ((e.currentTarget.selectionEnd ?? 0) === (e.currentTarget.value?.length || 0)) {
                        e.preventDefault()
                        const digits = onlyDigits(stripAnyPrefix(phone))
                        const trimmed = digits.slice(0, -1)
                        setPhone(p + formatLocal(trimmed, country))
                        return
                      }
                    }
                  }}
                  onChange={(e)=>{
                    const p = prefixFor(country)
                    const val = e.target.value
                    const digits = onlyDigits(stripAnyPrefix(val))
                    setPhone(p + formatLocal(digits, country))
                  }}
                  placeholder={countries[country]?.placeholder || `${t.phone[language]} (+${countries[country]?.dial || ''})`}
                  required
                  className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 hover:bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-black/10 montserrat"
                />
              </div>
            )}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e)=>setPassword(e.target.value)}
                placeholder={t.password[language]}
                required
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg bg-gray-50 hover:bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-black/10 montserrat"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
              </button>
            </div>
            {info && <div className="text-emerald-800 bg-emerald-50/70 border border-emerald-100 px-3 py-2.5 rounded-lg text-sm montserrat">{info}</div>}
            {error && <div className="text-red-700 bg-red-50/70 border border-red-100 px-3 py-2.5 rounded-lg text-sm montserrat">{error}</div>}
            {/* resend button removed by request */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-5 py-3.5 bg-black text-white rounded-lg montserrat disabled:opacity-60 hover:bg-neutral-900 transition-colors"
            >
              {mode==='signin' ? t.signin[language] : t.signup[language]}
            </button>
          </form>
          <div className="mt-5 text-center">
            <button onClick={()=>{ setMode(mode==='signin'?'signup':'signin'); setError(''); setInfo('') }} className="inline-flex items-center gap-2 text-sm underline underline-offset-4 hover:no-underline montserrat">
              {mode==='signin' ? t.noAcc[language] : t.haveAcc[language]}
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Auth
